import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { useJwtMutation } from './hooks/useCustomerQuery'

/**
 * Port of `app/customer/jwt.html` + `app/customer/jwtController.js`
 * (AngularJS `jwtController`) — the "Login Administrativo" screen, reached
 * at `/workspace/jwt` (legacy state `workspace.jwt`). Menu entry ("Login
 * Administrativo") already gated behind `allowedToGenerateJwt` in
 * `AppShell.jsx` (task 010, `useCurrentUser`'s port of `mainController`'s
 * `AuthService.getUser().then(...)` callback) — this task does not touch
 * that gating, only the destination screen, same pattern already documented
 * in `MenemosinePage.jsx` for its own `allowedToGenerateJwt`-gated menu
 * entry. There is nothing in `jwtController.js`/`jwt.html` itself that
 * re-checks the permission (no guard beyond the menu item's visibility), so
 * this page renders unconditionally once navigated to, matching legacy
 * behavior 1:1 — a user who reaches `/workspace/jwt` directly (e.g. by
 * typing the URL) could always generate a token in the legacy app too.
 *
 * Field-by-field mapping from the legacy template/controller:
 * - `$scope.token` (readonly `<input>`) -> local `token` state rendered in a
 *   readonly `TextField`.
 * - "Gerar" button (`ng-click="generate()"`) -> `Button` calling
 *   `useJwtMutation().mutate()` (`POST /jwt/generate`, see
 *   `./hooks/useCustomerQuery.js`). On success: legacy copies the token to
 *   the clipboard, shows a Materialize toast ("Token gerado e copiado para
 *   área de transferência."), then sets `$scope.token` -> ported as
 *   `navigator.clipboard.writeText` + MUI `Snackbar` + updating the `token`
 *   state, same order of operations. On error: legacy shows an error toast
 *   and logs to console -> ported the same way.
 * - `$scope.loading` is declared but never actually read anywhere in
 *   `jwt.html` (confirmed via `grep -n "loading" app/customer/jwt.html`, no
 *   hits) — same dead-state pattern already documented in
 *   `EditCustomerProfilePage.jsx`. Disabling "Gerar" while the mutation is
 *   in flight is still a direct, uncontroversial mapping of that unused
 *   flag's intent, not new behavior.
 * - `initView()` is a no-op in the legacy controller — nothing to port on
 *   mount.
 *
 * Not ported: the legacy template's `$('select').material_select()`
 * `$(document).ready` snippet — there is no `<select>` element anywhere on
 * this screen (confirmed by reading the full template), so it's dead
 * jQuery/Materialize initialization with nothing to attach to, same
 * category of dead presentational code already documented as "not ported"
 * in `CustomersListPage.jsx`/`EditCustomerProfilePage.jsx`/`plan.md`'s UI-kit
 * decision.
 *
 * Clipboard note: `navigator.clipboard?.writeText` is used directly, same as
 * the legacy controller itself already did (no hidden-textarea/
 * `document.execCommand('copy')` fallback exists in `jwtController.js` to
 * begin with — unlike `CustomerService.getFirstLoginToken`'s consumer,
 * documented in `useCustomerQuery.js`, this legacy screen already relied on
 * the modern Clipboard API). The optional-chaining guard matches the same
 * defensive call already made in `CustomersListPage.jsx`'s first-login-token
 * handler, for environments where `navigator.clipboard` may be unavailable
 * (e.g. non-secure contexts).
 */
function JwtLoginPage() {
  const [token, setToken] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })
  const jwtMutation = useJwtMutation()

  function notify(message, severity = 'info') {
    setSnackbar({ open: true, message, severity })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  /** Port of `generate()`. */
  function handleGenerate() {
    jwtMutation.mutate(undefined, {
      onSuccess: async (generatedToken) => {
        await navigator.clipboard?.writeText(generatedToken)
        notify('Token gerado e copiado para área de transferência.')
        setToken(generatedToken)
      },
      onError: (error) => {
        notify('Ocorreu um erro ao tentar gerar o Token.', 'error')
        console.error(error)
      },
    })
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Gerar Token JWT
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack spacing={2}>
          <TextField id="token" label="Token" value={token} slotProps={{ input: { readOnly: true } }} fullWidth />

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleGenerate} disabled={jwtMutation.isPending}>
              Gerar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default JwtLoginPage
